import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';

interface PieChartProps {
  data?: any,
  defaultValue?: string,
  height?: number,
}

const PieChart: React.FC<PieChartProps> = ({ data, defaultValue, height }) => {
  const chartRef = useRef<any>(null);

  useEffect(() => {
    // Update chart when defaultValue changes
    if (chartRef.current && chartRef.current.chart) {
      const chart = chartRef.current.chart;
      chart.hoveredPieValue = defaultValue;
      chart.redraw();
    }
  }, [defaultValue]);

  const options = {
    credits: {
      enabled: true,
      text: 'Powered by StockFortress',
      href: 'https://stockfortress.com',
      style: {
        fontSize: '10px',
        color: '#999999'
      }
    },
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: height,
      events: {
        load: function () {
          const chart = this;
          chart.hoveredPieValue = defaultValue || '0';
          chart.redraw();
          // Ensure proper layout in Chrome after initial render
          if (typeof chart.reflow === 'function') {
            setTimeout(() => chart.reflow(), 0);
          }
        },
        render: function () {
          const chart = this;
          if (chart.customLabel) {
            chart.customLabel.destroy();
          }
          if (chart.hoveredPieValue) {
            const centerX = chart.plotLeft + chart.plotWidth / 2;
            const centerY = chart.plotTop + chart.plotHeight / 2 + 5;

            chart.customLabel = chart.renderer.text(
              chart.hoveredPieValue,
              centerX,
              centerY
            )
              .css({
                color: "var(--text-primary)",
                fontSize: '20px',
                fontWeight: 'bold',
                textAnchor: 'middle'
              })
              .attr({
                align: 'center',
                zIndex: 10
              })
              .add();
          }
        }
      }
    },
    title: {
      enabled: false,
      text: '',
      style: {
        color: "var(--text-primary)",
        fontSize: '16px',
        fontWeight: 'bold',
      },
    },
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      series: {
        allowPointSelect: true,
        cursor: 'pointer',
        borderRadius: 0,
        dataLabels: [
          {
            enabled: true,
            distance: 10,
            format: '{point.name}',
            style: {
              fontSize: '11px',
              color: "var(--text-primary)",
            },
          },
          {
            enabled: true,
            distance: -25,
            format: '{point.percentage:.0f}%',
            style: {
              fontSize: '11px',
            },
          },
        ],
        showInLegend: false,
        point: {
          events: {
            mouseOver: function () {
              const chart = this.series.chart;
              chart.hoveredPieValue = this.formattedValue || `${this.percentage.toFixed(0)}%`;
              chart.redraw();
            },
            mouseOut: function () {
              const chart = this.series.chart;
              chart.hoveredPieValue = defaultValue;
              chart.redraw();
            }
          }
        }
      },
      pie: {
        innerSize: '50%',
        size: '90%'
      },
    },
    legend: {
      itemStyle: {
        color: "var(--text-primary)",
        fontSize: '0.9em',
      },
      itemHoverStyle: {
        color: '#f0f0f0',
      },
    },
    series: [
      {
        name: 'Categories',
        data: data
      },
    ],
  };
  return (
    <HighchartsReact
      ref={chartRef}
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { width: '100%', height: height ? height : undefined } }}
    />
  )

};
export default PieChart;